<template>
  <el-col :span="24" id="searchresult" v-loading.body="isLoading" element-loading-text="Loading...">
    <ListViewHeader
      title="Search Results"
      ref="listViewHeader"
      :meta="meta"
      :pageSize="50"
      :currentPage="currentPage"
      :changePage="changePage"
      :showFilters="showFilters"
      :toggleFilters="toggleFilters"
      :createRouteName="false"
      :showNewRoute="false"
      >
    </ListViewHeader>
    <el-row type="flex" justify="center" align="middle" style="padding: 0 12px;">
      <Filters
        ref="filters"
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="4"
        :autoApply="false"
        clearAction="clearSearchResultsState"
        clearMetadataAction="resetSearchResultsMeta"
        searchAction="fetchSearchData"
        @filterChanged="handleFilterChange"
        @filterCleared="() => $refs.filters.applyFilters()"
        @applied="handleAfterApply"
        :key="renderFilter"
        >
        <template v-for="(f, i) in filters" v-slot:[`${f.type}${i}`]="slotProps">
          <component :key="i" :is="f.type" :ref="`${f.type+'_ref'}`" :properties="f.props" />
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col>
        <el-row type="flex">
        <el-table
          v-if="meta && meta.shown && !changingPage"
          id="searchresult-table"
          ref="srsTable"
          :data="searchresult"
          border
          style="width: 100%; z-index: 0;"
          :maxHeight="tableHeight"
          @mounted="handleTableLoad"
        >
          <el-table-column prop="ledger" label="ID" align="center" width="120">
            <template #default="scope">
              <div v-if="scope.row.transaction_src !== 'PT' && scope.row.transaction_src !== 'CK'">
                <router-link
                  :to="{ name: getTransactionRouteName(scope.row.transaction_src), params: { id: scope.row.transaction_id } }"
                  target="_blank"
                >
                  {{ scope.row.transaction_ledger.split(' ')[0] || '--'}}
                </router-link>
              </div>
              <div v-else>
                <div style="white-space: normal; word-break: break-word; overflow-wrap: break-word;">
                  {{ scope.row.transaction_ledger.split(' ')[0] || '--'}}
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="transaction_src" label="Src" align="left" width="120" class-name="wrap-text">
            <template #default="scope">
              {{ scope.row.transaction_src || '--'}}
            </template>
          </el-table-column>

          <el-table-column prop="transaction_vendor" label="Vendor" align="left" width="200">
            <template #default="scope">
              <div style="white-space: normal; word-break: break-word; overflow-wrap: break-word;">
                {{ scope.row.transaction_vendor || scope.row.transaction_third_party_vendor || '--' }}
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="transaction_number" label="Doc #" align="left" width="120">
            <template #default="scope">
              <div style="white-space: normal; word-break: break-word; overflow-wrap: break-word;">
                {{ scope.row.transaction_number || '--' }}
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="transaction_effective_date" label="Effective Date" align="center" width="120">
            <template #default="scope">
              {{ intlDateFormatter(scope.row.transaction_effective_date) || '--'}}
            </template>
          </el-table-column>

          <el-table-column prop="PO_reference" label="PO" align="left" width="200">
            <template #default="scope">
              {{ scope.row.PO_number || scope.row.PO_reference || '--'}}
            </template>
          </el-table-column>

          <el-table-column prop="account" label="Account" align="left" width="200">
            <template #default="scope">
              {{ scope.row.account || '--'}}
            </template>
          </el-table-column>

          <el-table-column prop="amount_decimal" label="Amount" align="right" width="120">
            <template #default="scope">
              {{ formatAmount(scope.row.amount) }}
            </template>
          </el-table-column>

          <el-table-column prop="created_by" label="Created By" align="left" width="120">
            <template #default="scope">
              {{ scope.row.created_by || '--'}}
            </template>
          </el-table-column>

          <el-table-column prop="posting_date" label="Posting Date" align="center" width="120">
            <template #default="scope">
              {{ intlDateFormatter(scope.row.transaction_posting_date) || '--'}}
            </template>
          </el-table-column>

          <el-table-column prop="last_modified_by" label="Last Modified By" align="left" width="150">
            <template #default="scope">
              {{ scope.row.transaction_modified_by || '--'}}
            </template>
          </el-table-column>

          <el-table-column prop="modification_date" label="Modification Date" align="center" width="150">
            <template #default="scope">
              {{ intlDateFormatter(scope.row.transaction_modification_date) || '--'}}
            </template>
          </el-table-column>
        </el-table>
        </el-row>
        <el-row v-if="!meta.count && !searchresult.length" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import store from '@/store/store'
  import { mapGetters } from 'vuex'
  import Filters from '@/components/Filters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import ListViewHeader from '@/components/DataEntryListViewHeader'
  import { listViewMixins } from '@/Mixins/TransactionListViews'

  export default {
    components: {Filters, KeywordFilter, ListViewHeader},
    mixins: [ listViewMixins ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        watchRoute: true,
        renderFilter: 0,
        keywordSearch: '',
        uniqueAccounts: [],
        loading: false
      }
    },
    // Watch for changes in route query parameters
    watch: {
    },
    methods: {
      handleTableLoad () {
        const parentHeight = document.getElementById('searchresult-table').offsetHeight
        const headerHeight = document.getElementById('list-view-header').offsetHeight
        this.tableHeight = parentHeight - headerHeight
      },
      handleAfterApply () {
        if (this.keywordSearch === this.$store.getters.getFilters.search) return
        this.keywordSearch = this.$store.getters.getFilters.search
      },
      handleFilterChange (filter) {
        this.$store.dispatch('setFilter', { [`search`]: filter.selectedVal })
      },
      getTransactionRouteName (src) {
        if (!src) return
        let routeName = {
          'AP': 'EditAp',
          'AR': 'EditCpaar',
          'RT': 'EditRt',
          'PC': 'EditPc',
          'CC': 'EditCc',
          'JE': 'EditJe',
          'PR': 'EditCpr',
          'PO': 'EditPo'
        }
        return routeName[src]
      },
      /**
       * Handles paginating between AP pages
       * @param page  Target page
       */
      async changePage (page) {
        try {
          this.changingPage = true
          const { currentFilters } = this
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, { ...currentFilters, page })
            : Object.assign({}, { page })
          await this.$store.dispatch('clearSearchResultsState', [])
          const entities = await this.$store.dispatch('fetchSearchData', payload)
          if (entities) {
            const promises = await this.$store.dispatch('findDependencies', entities)
            await Promise.all(promises)
          }
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
        } catch (e) {
          console.log(e) // TODO: use a logger
          this.changingPage = false
        }
      },
      intlDateFormatter (date) {
        if (date) {
          const dt = date.split('-')
          return dt[1] + '-' + dt[2] + '-' + dt[0]
        } else {
          return ''
        }
      }
    },
    computed: {
      ...mapGetters({
        getVendorById: 'getVendorById',
        searchresultRaw: 'getSearchResults',
        meta: 'getSearchResultsMetadata',
        currentPage: 'getCurrentSearchResultsPage',
        currentFilters: 'getFilters',
        isLoading: 'getReceivingDataStatus'
      }),
      searchKey () {
        let search = this.$store.getters.getSearchKey || ''
        if (Object.keys(this.$refs).length) {
          this.$refs['keywordFilter_ref'][0].keyword = search
          this.$store.dispatch('setFilter', { search })
        }
        return search
      },
      searchresult () {
        return Array.isArray(this.searchresultRaw) ? this.searchresultRaw : Object.values(this.searchresultRaw || {})
      },
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {
              keyword: this.searchKey
            }
          }
        ]
      }
    },
    mounted () {
      if (Object.keys(this.$refs).length) {
        this.$refs['keywordFilter_ref'][0].keyword = this.searchKey
        this.$store.dispatch('setFilter', { searchKey: this.searchKey })
      }
    },
    async beforeDestroy () {
      await Promise.all([
        this.$store.dispatch('clearFilters')
        // this.$store.dispatch('updateTransactionSelection', []),
        // this.$store.dispatch('updateFilteredTransactionSelection', [])
      ])
    },
    async beforeRouteLeave (to, from, next) {
      try {
        await store.dispatch('clearPoState', true)
        // Clear the watcher before leaving the route
        this.watchRoute = false
        this.renderFilter = 0
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    }
  }
</script>

<style scoped>
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }

  .wrap-text {
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
  }
</style>


// WEBPACK FOOTER //
// src/components/RouteComponents/SearchResults.vue